import { MAX_STACK_SIZE } from './ItemDefinition';
import type { ItemId } from './ItemDefinition';

export interface ItemStack {
  itemId: ItemId;
  count: number;
}

export type InventorySnapshot = ReadonlyArray<ItemStack | null>;

export type InventoryListener = (snap: InventorySnapshot) => void;

export class Inventory {
  readonly capacity: number;
  readonly name: string;
  private readonly slots: (ItemStack | null)[];
  private readonly listeners = new Set<InventoryListener>();

  constructor(capacity: number, name: string) {
    this.capacity = capacity;
    this.name = name;
    this.slots = new Array(capacity).fill(null);
  }

  snapshot(): InventorySnapshot {
    return this.slots.map((s) => (s ? { itemId: s.itemId, count: s.count } : null));
  }

  subscribe(listener: InventoryListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Returns how many items were actually accepted (the rest overflowed). */
  addItem(itemId: ItemId, count: number): number {
    if (count <= 0) return 0;
    let remaining = count;

    // Fill existing stacks of the same item first.
    for (let i = 0; i < this.capacity && remaining > 0; i++) {
      const slot = this.slots[i];
      if (slot && slot.itemId === itemId && slot.count < MAX_STACK_SIZE) {
        const room = MAX_STACK_SIZE - slot.count;
        const add = Math.min(room, remaining);
        slot.count += add;
        remaining -= add;
      }
    }

    // Then fill empty slots.
    for (let i = 0; i < this.capacity && remaining > 0; i++) {
      if (!this.slots[i]) {
        const add = Math.min(MAX_STACK_SIZE, remaining);
        this.slots[i] = { itemId, count: add };
        remaining -= add;
      }
    }

    const added = count - remaining;
    if (added > 0) this.emit();
    return added;
  }

  getSlot(index: number): ItemStack | null {
    const slot = this.slots[index];
    return slot ? { itemId: slot.itemId, count: slot.count } : null;
  }

  /** Takes up to `count` items from slot. Returns the stack taken, or null. */
  takeFromSlot(index: number, count: number): ItemStack | null {
    const slot = this.slots[index];
    if (!slot) return null;
    const take = Math.min(slot.count, count);
    if (take <= 0) return null;
    const taken: ItemStack = { itemId: slot.itemId, count: take };
    slot.count -= take;
    if (slot.count <= 0) this.slots[index] = null;
    this.emit();
    return taken;
  }

  /**
   * Deposits a stack into a slot. Mergeable with existing same-itemId stack up
   * to MAX_STACK_SIZE. Returns the remainder that did not fit, or null if
   * everything fit.
   */
  depositToSlot(index: number, stack: ItemStack): ItemStack | null {
    if (index < 0 || index >= this.capacity) return stack;
    const existing = this.slots[index];
    if (!existing) {
      const place = Math.min(MAX_STACK_SIZE, stack.count);
      this.slots[index] = { itemId: stack.itemId, count: place };
      const rem = stack.count - place;
      this.emit();
      return rem > 0 ? { itemId: stack.itemId, count: rem } : null;
    }
    if (existing.itemId === stack.itemId) {
      const room = MAX_STACK_SIZE - existing.count;
      const place = Math.min(room, stack.count);
      existing.count += place;
      const rem = stack.count - place;
      if (place > 0) this.emit();
      return rem > 0 ? { itemId: stack.itemId, count: rem } : null;
    }
    // Different item in slot — caller should swap instead.
    return stack;
  }

  /** Wipe the inventory + replace it with a flat list of stacks (in
   *  slot order). Used by the save/load system to restore state. Slots
   *  past `capacity` are silently dropped — saves authored on a smaller
   *  inventory still load cleanly. */
  restoreSlots(slots: ReadonlyArray<ItemStack | null>): void {
    for (let i = 0; i < this.capacity; i++) {
      const incoming = slots[i] ?? null;
      this.slots[i] = incoming
        ? {
            itemId: incoming.itemId,
            count: Math.max(0, Math.min(MAX_STACK_SIZE, incoming.count))
          }
        : null;
    }
    this.emit();
  }

  /** Replaces the contents of a slot and returns the previous contents. */
  swapSlot(index: number, newStack: ItemStack | null): ItemStack | null {
    if (index < 0 || index >= this.capacity) return newStack;
    const old = this.slots[index] ?? null;
    this.slots[index] = newStack;
    this.emit();
    return old;
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) listener(snap);
  }
}
